import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { canRetryShipOrder, DISPATCH_STATUS, ORDER_STATUS } from "./labels";
import { TABLE, TD, TH } from "./tableStyles";
import { useTRPC } from "./trpc";

/**
 * The orders Loot OS created in this store for claimed items: fulfill them in Shopify like any
 * other order. One whose creation failed can be retried here.
 */
export function ShipOrdersScreen() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const orders = useQuery(trpc.shipOrders.list.queryOptions());
  const retry = useMutation(
    trpc.shipOrders.retry.mutationOptions({
      onSuccess: (result) => {
        shopify.toast.show(
          result.dispatchStatus === "created"
            ? "Order created."
            : (result.lastError ?? "Retry failed."),
          { isError: result.dispatchStatus !== "created" },
        );
        void queryClient.invalidateQueries({ queryKey: trpc.shipOrders.list.queryKey() });
      },
    }),
  );

  return (
    <s-stack gap="base">
      <s-paragraph>
        When a customer claims the item they revealed, Loot OS adds an order to your store with the
        item at its full price and a 100% LOOT-OS-CLAIM discount, so it totals $0: the box's own
        order already took the payment. It's tagged loot-os and ships to the address on their
        original order. Fulfill it in Shopify like any other order.
      </s-paragraph>
      {orders.isLoading ? (
        <s-paragraph>Loading…</s-paragraph>
      ) : (orders.data ?? []).length === 0 ? (
        <s-paragraph color="subdued">Nothing to ship yet.</s-paragraph>
      ) : (
        <table style={TABLE}>
          <thead>
            <tr>
              <th style={TH}>Shopify order</th>
              <th style={TH}>Order in your store</th>
              <th style={TH}>Shipping</th>
              <th style={TH}>Tracking</th>
              <th style={TH}>
                <s-text accessibilityVisibility="exclusive">Actions</s-text>
              </th>
            </tr>
          </thead>
          <tbody>
            {(orders.data ?? []).map((o) => (
              <tr key={o.id}>
                <td style={TD}>{o.shopifyOrderGid?.replace("gid://shopify/Order/", "#") ?? "—"}</td>
                <td style={TD}>
                  <s-badge tone={DISPATCH_STATUS[o.dispatchStatus].tone}>
                    {DISPATCH_STATUS[o.dispatchStatus].label}
                  </s-badge>
                </td>
                <td style={TD}>
                  <s-badge tone={ORDER_STATUS[o.orderStatus].tone}>
                    {ORDER_STATUS[o.orderStatus].label}
                  </s-badge>
                </td>
                <td style={TD}>{o.trackingNumber ?? "—"}</td>
                <td style={TD}>
                  {canRetryShipOrder(o) && (
                    <s-button
                      disabled={retry.isPending || undefined}
                      onClick={() => retry.mutate({ id: o.id })}
                    >
                      Try again
                    </s-button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </s-stack>
  );
}
